import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useTranslation } from "react-i18next";
import { fetchData } from "../api/client";

/**
 * Dialog de padrões de custo por modalidade (CLT, PJ, etc.).
 * Controlado por `open` / `onClose` — o gatilho fica na toolbar da página de cargos.
 */
export default function HrContractPresetsPanel({ open, onClose }) {
  const { t } = useTranslation("settings");
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ok, setOk] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    setOk(null);
    try {
      const res = await fetchData("/hr/contract-presets", "GET");
      const list = res?.data?.data || res?.data || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setError(e?.message || t("positionsPage.costDefaultsLoadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    load();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setOk(null);
    try {
      await fetchData("/hr/contract-presets", "PUT", {
        presets: rows.map((r) => ({
          modalidade: r.modalidade,
          label: r.label,
          encargos_pct_default: Number(r.encargos_pct_default) || 0,
          beneficios_mensal_default: Number(r.beneficios_mensal_default) || 0,
          notas: r.notas,
          ativo: r.ativo,
        })),
      });
      setOk(t("positionsPage.costDefaultsSaved"));
      await load();
    } catch (e) {
      setError(e?.message || t("positionsPage.costDefaultsSaveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  return (
    <Dialog
      open={Boolean(open)}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      aria-labelledby="hr-cost-defaults-title"
    >
      <DialogTitle
        id="hr-cost-defaults-title"
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
        }}
      >
        <Box>
          <Typography variant="h6" component="span" fontWeight={700}>
            {t("positionsPage.costDefaultsTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 520 }}>
            {t("positionsPage.costDefaultsSubtitle")}
          </Typography>
        </Box>
        <IconButton
          aria-label={t("positionsPage.costDefaultsClose")}
          onClick={handleClose}
          size="small"
          disabled={saving}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
              {t("positionsPage.costDefaultsHint")}
            </Alert>

            <Table size="small" sx={{ "& td, & th": { borderColor: "divider" } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 110 }}>
                    {t("positionsPage.costDefaultsColModality")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 130 }}>
                    {t("positionsPage.costDefaultsColCharges")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 150 }}>
                    {t("positionsPage.costDefaultsColBenefits")}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    {t("positionsPage.costDefaultsColNotes")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, idx) => (
                  <TableRow key={r.modalidade || idx} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {r.label || r.modalidade}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={r.encargos_pct_default ?? 0}
                        onChange={(e) => setRow(idx, { encargos_pct_default: e.target.value })}
                        inputProps={{ min: 0, step: 0.01, "aria-label": t("positionsPage.costDefaultsColCharges") }}
                        InputProps={{
                          endAdornment: (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              %
                            </Typography>
                          ),
                        }}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={r.beneficios_mensal_default ?? 0}
                        onChange={(e) => setRow(idx, { beneficios_mensal_default: e.target.value })}
                        inputProps={{ min: 0, step: 0.01, "aria-label": t("positionsPage.costDefaultsColBenefits") }}
                        InputProps={{
                          startAdornment: (
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                              R$
                            </Typography>
                          ),
                        }}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={r.notas || ""}
                        onChange={(e) => setRow(idx, { notas: e.target.value })}
                        placeholder={t("positionsPage.costDefaultsNotesPlaceholder")}
                        fullWidth
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {!rows.length && !error ? (
              <Typography variant="body2" color="text.secondary">
                {t("positionsPage.costDefaultsEmpty")}
              </Typography>
            ) : null}

            {error ? <Alert severity="error">{error}</Alert> : null}
            {ok ? <Alert severity="success">{ok}</Alert> : null}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={saving} color="inherit">
          {t("positionsPage.costDefaultsCancel")}
        </Button>
        <Tooltip title={t("positionsPage.costDefaultsSaveTooltip")}>
          <span>
            <Button variant="contained" onClick={save} disabled={saving || loading || !rows.length}>
              {saving ? t("positionsPage.costDefaultsSaving") : t("positionsPage.costDefaultsSave")}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
}
